import { useContext, useEffect, useMemo } from "react";
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import PinDropIcon from "@mui/icons-material/PinDrop";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useParams, useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CORPORATE_BACKOFFICE_PATHS } from "constants/constants";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import useMenuItemExpand from "components/Layout/AppDrawer/hooks/useMenuItemExpand";
import { useStyles } from "components/Layout/AppDrawer/components/DrawerItem.styles";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";

// Renders the corporation's location tree: mills directly under the corporation, plus
// collapsible Division > Region > Mill groups. Every row carries a fixed-width icon slot
// (chevron for groups, empty for leaf mills) so labels stay vertically aligned by depth.
//
// hideRoot: drop the outer "Locations" toggle and show the tree directly (used by the
// top-bar dropdown, whose trigger is already labelled "Locations"). hideRoot also switches
// to a tight indent scale suited to the narrow dropdown vs. the old wide side panel.
const LocationsMenu = ({ hideRoot = false }: { hideRoot?: boolean }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { isMenuItemExpanded, toggleMenuItemExpanded, addMenuItemsExpanded } = useMenuItemExpand();
    const navigate = useNavigate();
    const routeParams = useParams();
    const millId = useMemo(() => Number(routeParams.millId), [routeParams.millId]);
    const matchPath = useMatch(
        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.VARIABLE}`,
    );
    const {
        corporations,
        divisions,
        millsWithNoDivision,
        isLoadingCorporationsAndDivisions,
        refreshCorporationsAndDivisions,
    } = useContext(CorporateContext);
    const { handleShift } = useContext(HMIContext);

    // Indentation per depth (theme spacing unit = 4px). Tight scale for the dropdown,
    // wider scale for the legacy side panel.
    const indent = hideRoot ? { top: 2, region: 5, mill: 8 } : { top: 8, region: 16, mill: 24 };

    useEffect(() => {
        if (!isLoadingCorporationsAndDivisions && corporations) {
            const isInMillsWithNoDivision = millsWithNoDivision.some((mill) => mill.mill_id === millId);
            if (isInMillsWithNoDivision) {
                addMenuItemsExpanded(["locations"]);
                return;
            }

            corporations.forEach((corporation) => {
                (corporation.divisions || []).forEach((division) => {
                    const { division_id, regions } = division;
                    regions.forEach((region) => {
                        const { region_id, mills } = region;
                        mills?.forEach((mill) => {
                            if (mill.mill_id === millId) {
                                addMenuItemsExpanded(["locations", `division${division_id}`, `region${region_id}`]);
                            }
                        });
                    });
                });
            });
        } else if (matchPath) {
            addMenuItemsExpanded(["locations"]);
        }
    }, [isLoadingCorporationsAndDivisions, corporations, millsWithNoDivision]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        refreshCorporationsAndDivisions();
    }, [refreshCorporationsAndDivisions]);

    if (isLoadingCorporationsAndDivisions && !corporations) {
        return (
            <div className={classes.loadingMenu}>
                {t("corporate_backoffice.corporate_layout.app_drawer.locations_menu.loading")}
            </div>
        );
    }

    const locationsExpanded = hideRoot || isMenuItemExpanded("locations");

    const goToMill = (mill_id: number) => {
        navigate(`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${mill_id}`);
        handleShift(ShiftOptionsEnum.current);
    };

    // A clickable mill leaf. Empty icon slot keeps its label aligned with the headers above it.
    const renderMill = (mill: { mill_id: number; name?: string }, level: number) => (
        <ListItemButton
            key={mill.mill_id}
            sx={{ pl: level }}
            onClick={() => goToMill(mill.mill_id)}
            className={`${classes.drawerItem} ${millId === mill.mill_id ? classes.drawerItemSelected : ""}`}
        >
            <ListItemIcon />
            <ListItemText primary={mill.name} className={classes.drawerItemText} />
        </ListItemButton>
    );

    const renderCaret = (open: boolean) =>
        open ? <ExpandMore className={classes.chevronIcon} /> : <ChevronRightIcon className={classes.chevronIcon} />;

    return (
        <>
            {!hideRoot && (
                <ListItemButton onClick={() => toggleMenuItemExpanded("locations")} className={classes.drawerItem}>
                    <ListItemIcon>
                        <PinDropIcon className={classes.drawerItemIcon} />
                    </ListItemIcon>
                    <ListItemText
                        primary={t("corporate_backoffice.corporate_layout.app_drawer.locations_menu.title")}
                        className={classes.drawerItemText}
                    />
                    {isMenuItemExpanded("locations") ? (
                        <ExpandLess className={classes.chevronIcon} />
                    ) : (
                        <ExpandMore className={classes.chevronIcon} />
                    )}
                </ListItemButton>
            )}

            <Collapse in={locationsExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {/* Mills directly under the corporation (no division) */}
                    {millsWithNoDivision.map((mill) => renderMill(mill, indent.top))}

                    {/* Division > Region > Mill groups */}
                    {divisions.map((division) => {
                        const divisionOpen = isMenuItemExpanded(`division${division.division_id}`);
                        return (
                            <div key={division.division_id}>
                                <ListItemButton
                                    sx={{ pl: indent.top }}
                                    onClick={() => toggleMenuItemExpanded(`division${division.division_id}`)}
                                    className={classes.drawerItem}
                                >
                                    <ListItemIcon>{renderCaret(divisionOpen)}</ListItemIcon>
                                    <ListItemText primary={division.name} className={classes.divisionText} />
                                </ListItemButton>
                                <Collapse in={divisionOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {division.regions.map((region) => {
                                            const regionOpen = isMenuItemExpanded(`region${region.region_id}`);
                                            return (
                                                <div key={region.region_id}>
                                                    <ListItemButton
                                                        sx={{ pl: indent.region }}
                                                        onClick={() =>
                                                            toggleMenuItemExpanded(`region${region.region_id}`)
                                                        }
                                                        className={classes.drawerItem}
                                                    >
                                                        <ListItemIcon>{renderCaret(regionOpen)}</ListItemIcon>
                                                        <ListItemText
                                                            primary={region.name}
                                                            className={classes.regionText}
                                                        />
                                                    </ListItemButton>
                                                    <Collapse in={regionOpen} timeout="auto" unmountOnExit>
                                                        <List component="div" disablePadding>
                                                            {region.mills?.length ? (
                                                                region.mills.map((mill) =>
                                                                    renderMill(mill, indent.mill),
                                                                )
                                                            ) : (
                                                                <ListItemButton
                                                                    sx={{ pl: indent.mill }}
                                                                    className={classes.drawerItem}
                                                                >
                                                                    <ListItemIcon />
                                                                    <ListItemText
                                                                        primary={`(${t(
                                                                            "corporate_backoffice.corporate_layout.app_drawer.locations_menu.no_mills_found",
                                                                        )})`}
                                                                        className={classes.drawerItemTextNoData}
                                                                    />
                                                                </ListItemButton>
                                                            )}
                                                        </List>
                                                    </Collapse>
                                                </div>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            </div>
                        );
                    })}
                </List>
            </Collapse>
        </>
    );
};

export default LocationsMenu;
